/**
 * Sidebar layout-creation helpers for the PDP flow.
 *
 * Mirror handleCreatePdpAiLayout / handleCreatePdpLayeredImageLayout from
 * NetworksPageV2.vue with three differences:
 *   - `layout_id` is taken from the currently-open layout via the adapter
 *     and sent on EVERY request so new templates are appended to this layout
 *     (rather than accumulating into a new one).
 *   - On success, the sidebar refreshes the layout (adapter.refreshLayout())
 *     instead of navigating to a new layout URL.
 *   - On failure, the "Create layout" button message is re-enabled so the
 *     user can retry (POR-12590 issue #2 parity).
 */

function normalizeImageUrlForLayout(value) {
  if (!value || typeof value !== 'string') return '';

  const trimmed = value.trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('/share?file=')) {
    return trimmed;
  }

  try {
    const url = new URL(trimmed, window.location.origin);
    if (url.pathname === '/share' && url.searchParams.has('file')) {
      return `/share?file=${url.searchParams.get('file')}`;
    }
    if (/^(https?:)?\/\//i.test(trimmed)) {
      return trimmed;
    }
  } catch (e) {
    // Keep non-URL paths below.
  }

  if (trimmed.startsWith('/')) {
    return trimmed;
  }

  return trimmed;
}

/**
 * Flat AI Generate layout: routes each image through either the layered or
 * flat endpoint depending on its per-image mode, and appends to the current
 * layout via `layout_id`.
 */
export async function createPdpAiLayoutInCurrentLayout(adapter, messageIndex) {
  const messages = adapter.getMessages();
  const message = messages[messageIndex];
  const images = message?._pdpGeneratedImages || [];
  if (!images.length) return;

  const layoutId = adapter.getCurrentLayoutId();

  adapter.setProcessing(true);
  adapter.addMessage({
    role: 'assistant',
    content: `Adding ${images.length} PDP image${images.length > 1 ? 's' : ''} to this layout...`,
  });
  adapter.saveMessages();

  try {
    const parsedPrompt = adapter.getParsedPrompt();
    const productRefs = (parsedPrompt.product_references || []).filter((r) => !r._deselected);
    const firstProductUrl = productRefs.find((r) => r.type === 'url')?.value || null;

    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const path = normalizeImageUrlForLayout(img.url);

      const imageMsg = adapter.getMessages().find((m) => m._pdpImageContext?.type === img.type);
      const mode = imageMsg?._pdpImageContext?.productMode || 'integrated';
      const isLayered = mode === 'separate';

      // Use the same overlay URL the queue already prepared for this image
      // type. The queue stores it in `overlayUrl` after picking the right
      // packshot per type (simplified for GS1, raw for everything else),
      // so reusing it keeps the layout pixel-perfect with the chat preview
      // and avoids leaking the simplified GS1 packshot into other image
      // types like Lifestyle / How To / Ingredients (which need the raw
      // transparent-background packshot, not the white-bg simplified one).
      const ctxOverlay = imageMsg?._pdpImageContext?.overlayUrl || null;
      const overlayForLayout = ctxOverlay || firstProductUrl;

      let data;
      if (isLayered) {
        const sizeLabel = imageMsg?._pdpImageContext?.size || '';
        const [w, h] = sizeLabel.split('x').map(Number);
        const payload = {
          product_name: img.label || 'PDP Image',
          image_url: path,
          product_mode: 'separate',
          product_image_url: overlayForLayout,
          product_references: productRefs,
          image_type: img.type,
          skip_text_extraction: true,
        };
        if (w && h) {
          payload.template_width = w;
          payload.template_height = h;
        }
        if (layoutId) payload.layout_id = layoutId;
        ({ data } = await axios.post('/networks/create-layered-pdp', payload));
      } else {
        const payload = {
          product_name: img.label || 'PDP Image',
          image_url: path,
          use_image_size: 1,
        };
        if (layoutId) payload.layout_id = layoutId;
        ({ data } = await axios.post('/banner/0/group/create-layout-from-single-image', payload));
      }

      if (!data?.success) {
        throw new Error('Layout creation returned unsuccessful response.');
      }
    }

    adapter.addMessage({
      role: 'assistant',
      content: `Added <b>${images.length}</b> PDP image${images.length > 1 ? 's' : ''} to this layout. Reloading...`,
    });
    adapter.saveMessages();
    await adapter.refreshLayout();
  } catch (e) {
    adapter.setMessageDisabled(messageIndex, false);
    adapter.addMessage({
      role: 'assistant',
      content: 'Failed to add the PDP image to this layout. Please try again.',
    });
    adapter.saveMessages();
  } finally {
    adapter.setProcessing(false);
  }
}

/**
 * Layered Image layout: every image goes through /networks/create-layered-pdp
 * with the user-picked mode (Layered or Integrated) per image, appended to the
 * current layout via `layout_id`.
 */
export async function createPdpLayeredLayoutInCurrentLayout(adapter, messageIndex) {
  const messages = adapter.getMessages();
  const message = messages[messageIndex];
  const images = message?._pdpGeneratedImages || [];
  if (!images.length) return;

  const layoutId = adapter.getCurrentLayoutId();

  adapter.setProcessing(true);
  adapter.addMessage({
    role: 'assistant',
    content: `Adding ${images.length} image${images.length > 1 ? 's' : ''} to this layout...`,
  });
  adapter.saveMessages();

  try {
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const path = normalizeImageUrlForLayout(img.url);

      const parsedPrompt = adapter.getParsedPrompt();
      const productRefs = (parsedPrompt.product_references || []).filter((r) => !r._deselected);
      const firstProductUrl = productRefs.find((r) => r.type === 'url')?.value || null;

      const imageMsg = adapter.getMessages().find((m) => m._pdpImageContext?.type === img.type);
      const effectiveMode = imageMsg?._pdpImageContext?.productMode || 'integrated';
      const isSeparate = effectiveMode === 'separate';

      // Use the same overlay URL the queue already prepared for this image
      // type. The queue stores it in `overlayUrl` after picking the right
      // packshot per type (simplified for GS1, raw for everything else),
      // so reusing it keeps the layout pixel-perfect with the chat preview
      // and avoids leaking the simplified GS1 packshot into other image
      // types like Lifestyle / How To / Ingredients (which need the raw
      // transparent-background packshot, not the white-bg simplified one).
      const ctxOverlay = imageMsg?._pdpImageContext?.overlayUrl || null;
      const overlayForLayout = ctxOverlay || firstProductUrl;

      const sizeLabel = parsedPrompt.pdp_layered_size || '';
      const [selectedWidth, selectedHeight] = sizeLabel.split('x').map(Number);

      const payload = {
        product_name: img.label || 'PDP Image',
        image_url: path,
        product_mode: effectiveMode,
        product_image_url: isSeparate ? overlayForLayout : null,
        product_references: isSeparate ? productRefs : [],
        image_type: img.type,
      };
      // Hero / Cambridge / GS1 in Layered mode: the background is generated
      // as plain white, so there's nothing for text extraction to find.
      // Skipping prevents Gemini inpainting from hallucinating unrelated
      // content (we've seen DivaCare medical ads, scenes, watermarks, etc.
      // appear in production environments where ChatGPT Vision creds exist).
      const blankBgTypes = new Set(['hero', 'mobile_hero_cambridge', 'mobile_hero_gs1']);
      if (blankBgTypes.has(img.type) && isSeparate) {
        payload.skip_text_extraction = true;
      }
      if (selectedWidth && selectedHeight) {
        payload.template_width = selectedWidth;
        payload.template_height = selectedHeight;
      }
      if (layoutId) {
        payload.layout_id = layoutId;
      }

      adapter.addMessage({
        role: 'assistant',
        content: `Creating template <b>#${i + 1}</b>: <b>${img.label}</b>...`,
      });
      adapter.saveMessages();

      const requestPromise = axios.post('/networks/create-layered-pdp', payload);
      const layersTimeout = setTimeout(() => {
        adapter.addMessage({
          role: 'assistant',
          content: `Creating editable layers for <b>${img.label}</b>...`,
        });
        adapter.saveMessages();
      }, 2000);

      let data;
      try {
        ({ data } = await requestPromise);
      } finally {
        clearTimeout(layersTimeout);
      }

      if (!data?.success) {
        throw new Error('Layered PDP creation returned unsuccessful response.');
      }

      adapter.addMessage({
        role: 'assistant',
        content: `<b>${img.label}</b> ready (${i + 1} of ${images.length}).`,
      });
      adapter.saveMessages();
    }

    adapter.addMessage({
      role: 'assistant',
      content: 'All PDP images added to this layout. Reloading...',
    });
    adapter.saveMessages();
    await adapter.refreshLayout();
  } catch (e) {
    adapter.setMessageDisabled(messageIndex, false);
    adapter.addMessage({
      role: 'assistant',
      content: 'Failed to add the PDP image to this layout. Please try again.',
    });
    adapter.saveMessages();
  } finally {
    adapter.setProcessing(false);
  }
}
